'use client'

import { QueryParameters } from '@/lib/query-parameters'
import { StructuredLogger } from '@/lib/logger/types'
import { useSearchParams } from 'next/navigation'
import { PropsWithChildren, useEffect } from 'react'
import { useDepartureStation } from '@/components/business/printer-assignment/hooks/useDepartureStation'
import { usePositionKey } from '@/components/business/printer-assignment/hooks/usePositionKey'
import { useBridgeWebsocketPort } from '@/components/business/scanners/hooks/useBridgeWebsocketPort'
import { useLogger } from '@/components/utils/LoggerProvider'
import { useBgrSessionName } from '@/components/business/scanners/hooks/useBgrSessionName'
import { useBagTagPrinter } from '@/components/business/printer-assignment/hooks/useBagTagPrinter'
import { useStationNumeric } from '@/components/business/printer-assignment/hooks/useStationNumeric'
import { useBoardingPassPrinter } from '@/components/business/printer-assignment/hooks/useBoardingPassPrinter'

const dedicatedPositionKeyRegex = /^([^*]*)(?:AD|AP)\*([^*]+)$/i

// Position keys for dedicated PCs have 'AD*' or 'AP*' in the middle of them, e.g.: PHXAD*141456
// The 'AD*' or 'AP*' needs to be removed, as other systems do not include that, so
// a position key such as 'PHXAD*141456' should be converted to 'PHX141456'.
// But common use PCs just use the computer name instead of a position key and computer names
// are usually just strings without special characters such as '*'. If ACE is running on
// a common use PC, the position key doesn't need to be modifed.
const parseRawPositionKey = (
  rawPositionKey: string,
  airportCode: string | null,
  logger: StructuredLogger
) => {
  const result = dedicatedPositionKeyRegex.exec(rawPositionKey)

  if (result) {
    // The position key contains AD* or AP*, so we're probably running on
    // a dedicated PC.
    // result[1] contains the capturing group that captures everything before the 'AD*' or 'AP*',
    // usually that's the airport code
    // result[2] contains everything after 'AD*' or 'AP*'
    const parsedPositionKey = `${airportCode ?? result[1]}${result[2]}`
    logger.info({
      scope: 'src/components/utils/ProcessSearchParams::parseRawPositionKey',
      detail: 'parsed-position-key-for-dedicated-pc',
      metadata: {
        rawPositionKey,
        parsedPositionKey,
      },
    })
    return parsedPositionKey
  }
  // If we get here, we're probably running on a common use PC.
  logger.info({
    scope: 'src/components/utils/ProcessSearchParams::parseRawPositionKey',
    detail: 'raw-position-key-for-common-use',
    metadata: {
      rawPositionKey,
    },
  })
  return rawPositionKey
}

export function ProcessSearchParams({ children }: PropsWithChildren) {
  const rawPositionKey = useSearchParams().get(QueryParameters.POSITION_KEY)
  const airportCode = useSearchParams().get(QueryParameters.AIRPORT_CODE)
  const bridgeWebsocketPort = useSearchParams().get(QueryParameters.WEBSOCKET_BRIDGE_PORT)
  const bgrSessionName = useSearchParams().get(QueryParameters.BGR_SESSION_NAME)
  const bagTagPrinter = useSearchParams().get(QueryParameters.BAG_TAG_PRINTER)
  const boardingPassPrinter = useSearchParams().get(QueryParameters.BOARDING_PASS_PRINTER)
  const stationNumeric = useSearchParams().get(QueryParameters.STATION_NUMERIC)

  const [, setPositionKey] = usePositionKey()
  const [, setDepartureStation] = useDepartureStation()
  const [, setBridgeWebsocketPort] = useBridgeWebsocketPort()
  const [, setBgrSessionName] = useBgrSessionName()
  const [, setBagTagPrinter] = useBagTagPrinter()
  const [, setBoardingPassPrinter] = useBoardingPassPrinter()
  const [, setStationNumeric] = useStationNumeric()

  const { logger, type } = useLogger()

  useEffect(() => {
    if (stationNumeric) setStationNumeric(stationNumeric.trim())
  }, [stationNumeric, setStationNumeric])

  useEffect(() => {
    if (bagTagPrinter) setBagTagPrinter(bagTagPrinter.trim())
  }, [bagTagPrinter, setBagTagPrinter])

  useEffect(() => {
    if (boardingPassPrinter) setBoardingPassPrinter(boardingPassPrinter.trim())
  }, [boardingPassPrinter, setBoardingPassPrinter])

  useEffect(() => {
    if (airportCode) setDepartureStation(airportCode.trim())
  }, [airportCode, setDepartureStation])

  useEffect(() => {
    if (bridgeWebsocketPort) setBridgeWebsocketPort(parseInt(bridgeWebsocketPort ?? '7980', 10))
  }, [bridgeWebsocketPort, setBridgeWebsocketPort])

  useEffect(() => {
    if (bgrSessionName) setBgrSessionName(bgrSessionName.trim())
  }, [bgrSessionName, setBgrSessionName])

  useEffect(() => {
    if (type !== 'app-insights') return
    if (rawPositionKey) {
      const positionKey = parseRawPositionKey(rawPositionKey, airportCode, logger)
      if (positionKey) {
        setPositionKey(positionKey)
      }
    }
  }, [rawPositionKey, airportCode, setPositionKey, logger, type])

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>
}
